import { PageHeader } from '@/shared/components/ui/PageHeader';
// ─────────────────────────────────────────────────────────────────────────────
// BIZPLUS PARITY — BizMail  (Phase 5.3)
//
// Lists, subscribers, templates, campaigns and stats.
//
// Two things this screen refuses to let the user do quietly:
//   · send a campaign that fails the compliance check — the button stays
//     disabled and the reasons are shown, because a bulk mail with no working
//     unsubscribe gets the sending domain blocklisted
//   · misread the audience — "1,200 on the lists, 1,140 will be mailed" is
//     broken down by reason rather than silently sending fewer than expected
// ─────────────────────────────────────────────────────────────────────────────

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { toast } from 'react-hot-toast';
import {
  Mail, Users, FileText, Send, BarChart3, AlertTriangle, Ban, Plus, Upload, Eye,
} from 'lucide-react';

type Tab = 'campaigns' | 'lists' | 'templates' | 'suppressions';

const TABS: Array<{ key: Tab; label: string; icon: typeof Mail }> = [
  { key: 'campaigns', label: 'Campaigns', icon: Send },
  { key: 'lists', label: 'Lists & subscribers', icon: Users },
  { key: 'templates', label: 'Templates', icon: FileText },
  { key: 'suppressions', label: 'Suppressed', icon: Ban },
];

const num = (v: unknown) => Number(v || 0).toLocaleString('en-IN');

export function CampaignsPage() {
  const [tab, setTab] = useState<Tab>('campaigns');

  return (
    <div className="max-w-[1580px] mx-auto space-y-6 pb-16">
      <PageHeader icon={Mail} eyebrow="Administration" title="BizMail" description={<>Email campaigns, mailing lists and delivery stats.</>} />

      <div className="flex gap-6 border-b border-outline text-xs font-semibold overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`pb-3 flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${
              tab === key ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === 'campaigns' && <CampaignsTab />}
      {tab === 'lists' && <ListsTab />}
      {tab === 'templates' && <TemplatesTab />}
      {tab === 'suppressions' && <SuppressionsTab />}
    </div>
  );
}

// ── Campaigns ────────────────────────────────────────────────────────────────
function CampaignsTab() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string>('');
  const [creating, setCreating] = useState(false);

  const { data } = useQuery({
    queryKey: ['campaigns', 'list'],
    queryFn: async () => (await api.get('/api/campaigns/campaigns')).data,
    refetchInterval: 10000, // a sending campaign's stats move while you watch
  });
  const { data: lists } = useQuery({
    queryKey: ['campaigns', 'lists'],
    queryFn: async () => (await api.get('/api/campaigns/lists')).data,
  });

  const items = data?.items || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setCreating(true)}
          className="px-3 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> New campaign
        </button>
      </div>

      {creating && (
        <CampaignForm
          lists={lists?.items || []}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); qc.invalidateQueries({ queryKey: ['campaigns', 'list'] }); }}
        />
      )}

      {items.length === 0 ? (
        <Empty>No campaigns yet.</Empty>
      ) : (
        <div className="space-y-2">
          {items.map((c: any) => (
            <div key={c._id} className="rounded-xl border border-outline bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{c.name}</span>
                    <StatusChip status={c.status} />
                  </div>
                  <p className="text-xs text-on-surface-variant mt-1">{c.subject || '(no subject)'}</p>
                </div>
                <button onClick={() => setSelected(selected === c._id ? '' : c._id)}
                  className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold flex items-center gap-1.5">
                  <BarChart3 className="w-3.5 h-3.5" /> {selected === c._id ? 'Hide' : 'Details'}
                </button>
              </div>

              <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-3 text-xs">
                {(['audience', 'sent', 'opened', 'clicked', 'bounced', 'unsubscribed'] as const).map((k) => (
                  <div key={k}>
                    <div className="text-on-surface-variant capitalize">{k}</div>
                    <div className="font-semibold text-sm">{num(c.stats?.[k])}</div>
                  </div>
                ))}
              </div>

              {selected === c._id && <CampaignDetail campaign={c} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignDetail({ campaign }: { campaign: any }) {
  const qc = useQueryClient();
  const [testTo, setTestTo] = useState('');

  const { data: preview, isLoading } = useQuery({
    queryKey: ['campaigns', 'preview', campaign._id],
    queryFn: async () => (await api.get(`/api/campaigns/campaigns/${campaign._id}/preview`)).data,
  });

  const send = useMutation({
    mutationFn: async () => (await api.post(`/api/campaigns/campaigns/${campaign._id}/send`)).data,
    onSuccess: () => { toast.success('Sending started.'); qc.invalidateQueries({ queryKey: ['campaigns', 'list'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not send.'),
  });
  const test = useMutation({
    mutationFn: async () => (await api.post(`/api/campaigns/campaigns/${campaign._id}/test`, { to: testTo })).data,
    onSuccess: (d: any) => toast.success(`Test sent to ${d.to}`),
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Test send failed.'),
  });

  if (isLoading) return <p className="mt-4 text-xs text-on-surface-variant">Loading…</p>;

  const alreadySent = ['sent', 'sending'].includes(campaign.status);
  const skipped: Record<string, number> = preview?.skipped || {};
  const skippedTotal = Object.values(skipped).reduce((a, b) => a + b, 0);

  return (
    <div className="mt-4 pt-4 border-t border-outline space-y-4">
      {/* Blocking problems, shown before anything else. */}
      {preview && !preview.ok && (
        <div className="p-3 rounded-lg bg-danger/10 dark:bg-danger border border-danger/25 dark:border-danger/40 text-xs text-danger-text dark:text-danger-text">
          <p className="font-semibold flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-3.5 h-3.5" /> This campaign cannot be sent yet
          </p>
          <ul className="list-disc ml-5 space-y-0.5">
            {preview.issues.map((i: string) => <li key={i}>{i}</li>)}
          </ul>
        </div>
      )}

      <div className="text-xs">
        <span className="font-semibold">{num(preview?.audience)}</span> will be mailed
        {skippedTotal > 0 && (
          <span className="text-on-surface-variant">
            {' '}· {num(skippedTotal)} not mailed ({Object.entries(skipped).map(([k, v]) => `${v} ${k}`).join(', ')})
          </span>
        )}
      </div>

      {preview?.preview && (
        <div className="rounded-lg border border-outline overflow-hidden">
          <div className="px-3 py-2 bg-surface-variant/30 text-xs font-semibold flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> {preview.preview.subject}
          </div>
          {/* Rendered as text, not injected as HTML: this is a preview of mail
              built from contact data, and the page must not execute it. */}
          <pre className="p-3 text-[11px] whitespace-pre-wrap break-all max-h-52 overflow-y-auto text-on-surface-variant">
            {preview.preview.html}
          </pre>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          placeholder="you@company.com"
          className="px-3 py-1.5 rounded-lg border border-outline bg-transparent text-xs"
        />
        <button onClick={() => test.mutate()} disabled={test.isPending}
          className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold disabled:opacity-40">
          Send test
        </button>
        <button
          onClick={() => send.mutate()}
          disabled={!preview?.ok || alreadySent || send.isPending}
          title={alreadySent ? `Already ${campaign.status}` : undefined}
          className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40"
        >
          <Send className="w-3.5 h-3.5" />
          {alreadySent ? `Already ${campaign.status}` : `Send to ${num(preview?.audience)}`}
        </button>
      </div>
    </div>
  );
}

function CampaignForm({ lists, onClose, onSaved }: { lists: any[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: '', subject: '', fromName: '', fromEmail: '', listIds: [] as string[], templateId: '',
    bodyHtml: '<p>Hi {{firstName}},</p>\n<p>…</p>\n<p><a href="{{unsubscribeUrl}}">Unsubscribe</a></p>',
  });

  const { data: templates } = useQuery({
    queryKey: ['campaigns', 'templates'],
    queryFn: async () => (await api.get('/api/campaigns/templates')).data,
  });

  const save = useMutation({
    mutationFn: async () => (await api.post('/api/campaigns/campaigns', form)).data,
    onSuccess: () => { toast.success('Campaign created.'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not create the campaign.'),
  });

  /** Copies the template's content in. The campaign keeps its own copy from
   *  here on, so editing the template later cannot rewrite a campaign that has
   *  already gone out. */
  const applyTemplate = (id: string) => {
    const t = (templates?.items || []).find((x: any) => x._id === id);
    setForm((f) => ({
      ...f,
      templateId: id,
      subject: t?.subject || f.subject,
      bodyHtml: t?.bodyHtml || f.bodyHtml,
    }));
  };

  return (
    <div className="rounded-xl border border-outline bg-surface p-4 space-y-3">
      <h3 className="text-sm font-bold">New campaign</h3>

      {(templates?.items || []).length > 0 && (
        <label className="block">
          <span className="text-xs font-semibold block mb-1">Start from a template</span>
          <select
            value={form.templateId}
            onChange={(e) => applyTemplate(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 rounded-lg border border-outline bg-transparent text-sm"
          >
            <option value="">Write from scratch</option>
            {templates.items.map((t: any) => <option key={t._id} value={t._id}>{t.name}</option>)}
          </select>
        </label>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <Text label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Text label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })}
          hint="{{firstName}} and {{company}} work here too." />
        <Text label="From name" value={form.fromName} onChange={(v) => setForm({ ...form, fromName: v })} />
        <Text label="From email" value={form.fromEmail} onChange={(v) => setForm({ ...form, fromEmail: v })} />
      </div>

      <div>
        <label className="text-xs font-semibold block mb-1">Lists</label>
        <div className="flex flex-wrap gap-2">
          {lists.map((l) => {
            const on = form.listIds.includes(l._id);
            return (
              <button key={l._id} type="button"
                onClick={() => setForm({
                  ...form,
                  listIds: on ? form.listIds.filter((x) => x !== l._id) : [...form.listIds, l._id],
                })}
                className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                  on ? 'border-primary text-primary bg-primary/10' : 'border-outline text-on-surface-variant'
                }`}>
                {l.name} ({num(l.counts?.subscribed)})
              </button>
            );
          })}
        </div>
        {lists.length === 0 && <p className="text-xs text-on-surface-variant">Create a list first.</p>}
      </div>

      <div>
        <label className="text-xs font-semibold block mb-1">Body (HTML)</label>
        <textarea value={form.bodyHtml} onChange={(e) => setForm({ ...form, bodyHtml: e.target.value })}
          rows={8} className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-xs font-mono" />
        <p className="text-[11px] text-on-surface-variant mt-1">
          Must contain <code>{'{{unsubscribeUrl}}'}</code> — a campaign without it cannot be sent.
          Available: {'{{name}} {{firstName}} {{company}} {{city}} {{phone}}'}
        </p>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold">Cancel</button>
        <button onClick={() => save.mutate()} disabled={save.isPending}
          className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold disabled:opacity-40">
          Create
        </button>
      </div>
    </div>
  );
}

// ── Lists ────────────────────────────────────────────────────────────────────
function ListsTab() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [importInto, setImportInto] = useState('');
  const [csv, setCsv] = useState('');

  const { data } = useQuery({
    queryKey: ['campaigns', 'lists'],
    queryFn: async () => (await api.get('/api/campaigns/lists')).data,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post('/api/campaigns/lists', { name })).data,
    onSuccess: () => { setName(''); toast.success('List created.'); qc.invalidateQueries({ queryKey: ['campaigns', 'lists'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not create the list.'),
  });

  const doImport = useMutation({
    mutationFn: async () => {
      // "email, name, company, city" per line. Parsed here so the user can paste
      // straight from a spreadsheet without a file upload step.
      const rows = csv.split('\n').map((line) => {
        const [email, n, company, city] = line.split(',').map((x) => (x || '').trim());
        return { email, name: n, company, city };
      }).filter((r) => r.email);
      return (await api.post(`/api/campaigns/lists/${importInto}/import`, { rows })).data;
    },
    onSuccess: (d: any) => {
      setCsv(''); setImportInto('');
      // Every category is reported: a silent "added 40 of 100" is how people
      // discover months later that their list was never complete.
      toast.success(`Added ${d.added}. ${d.duplicates} already present, `
        + `${d.skippedSuppressed} opted out, ${d.skippedInvalid} invalid.`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['campaigns', 'lists'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Import failed.'),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New list name"
          className="px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
        <button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}
          className="px-3 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold disabled:opacity-40">
          Create list
        </button>
      </div>

      {(data?.items || []).length === 0 ? <Empty>No lists yet.</Empty> : (
        <div className="space-y-2">
          {data.items.map((l: any) => (
            <div key={l._id} className="rounded-xl border border-outline bg-surface p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-sm">{l.name}</p>
                  <p className="text-xs text-on-surface-variant mt-0.5">
                    {num(l.counts?.subscribed)} subscribed · {num(l.counts?.unsubscribed)} opted out
                    {' '}· {num(l.counts?.bounced)} bounced
                  </p>
                </div>
                <button onClick={() => setImportInto(importInto === l._id ? '' : l._id)}
                  className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold flex items-center gap-1.5">
                  <Upload className="w-3.5 h-3.5" /> Import
                </button>
              </div>

              {importInto === l._id && (
                <div className="mt-3 pt-3 border-t border-outline space-y-2">
                  <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={6}
                    placeholder={'email, name, company, city\nasha@acme.com, Asha Pillai, Acme, Pune'}
                    className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-xs font-mono" />
                  <p className="text-[11px] text-on-surface-variant">
                    Addresses that have opted out are never re-added.
                  </p>
                  <button onClick={() => doImport.mutate()} disabled={!csv.trim() || doImport.isPending}
                    className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold disabled:opacity-40">
                    Import
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Templates ────────────────────────────────────────────────────────────────
const MERGE_VARS = ['name', 'firstName', 'email', 'company', 'city', 'phone', 'companyName'];

const BLANK_TEMPLATE = {
  _id: '', name: '', subject: '',
  bodyHtml: '<p>Hi {{firstName}},</p>\n<p>…</p>\n<p>{{companyName}}</p>\n'
    + '<p style="font-size:12px;color:#666">\n'
    + '  <a href="{{unsubscribeUrl}}">Unsubscribe</a>\n</p>',
};

function TemplatesTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any>(null);

  const { data } = useQuery({
    queryKey: ['campaigns', 'templates'],
    queryFn: async () => (await api.get('/api/campaigns/templates')).data,
  });

  const save = useMutation({
    mutationFn: async (t: any) => (t._id
      ? (await api.patch(`/api/campaigns/templates/${t._id}`, t)).data
      : (await api.post('/api/campaigns/templates', t)).data),
    onSuccess: () => {
      toast.success('Template saved.');
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['campaigns', 'templates'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not save the template.'),
  });

  const items = data?.items || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setEditing({ ...BLANK_TEMPLATE })}
          className="px-3 py-2 rounded-lg bg-primary text-on-primary text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> New template
        </button>
      </div>

      {editing && (
        <TemplateEditor
          value={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => save.mutate(editing)}
          saving={save.isPending}
        />
      )}

      {items.length === 0 && !editing ? <Empty>No templates yet.</Empty> : (
        <div className="space-y-2">
          {items.map((t: any) => (
            <div key={t._id} className="rounded-xl border border-outline bg-surface p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-sm">{t.name}</p>
                <p className="text-xs text-on-surface-variant mt-0.5">{t.subject || '(no subject)'}</p>
              </div>
              <button onClick={() => setEditing({ ...t })}
                className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold shrink-0">
                Edit
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Template editor.
 *
 * The compliance check runs as you type rather than at send time, because
 * discovering a missing unsubscribe link only when you press send — with the
 * audience already picked — is how people end up pasting one in carelessly.
 */
function TemplateEditor({ value, onChange, onCancel, onSave, saving }: {
  value: any; onChange: (v: any) => void; onCancel: () => void; onSave: () => void; saving: boolean;
}) {
  const [showPreview, setShowPreview] = useState(false);

  const hasUnsub = /\{\{\s*unsubscribeUrl\s*\}\}/.test(value.bodyHtml || '');
  const used = [...String(value.bodyHtml || '').matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g)].map((m) => m[1]);
  const unknown = [...new Set(used.filter((v) => v !== 'unsubscribeUrl' && !MERGE_VARS.includes(v)))];
  const canSave = Boolean(String(value.name || '').trim()) && hasUnsub;

  /** Insert at the cursor, so a variable lands where the writer is looking. */
  const insert = (variable: string) => {
    const el = document.getElementById('tpl-body') as HTMLTextAreaElement | null;
    const token = `{{${variable}}}`;
    if (!el) return onChange({ ...value, bodyHtml: (value.bodyHtml || '') + token });
    const start = el.selectionStart ?? (value.bodyHtml || '').length;
    const end = el.selectionEnd ?? start;
    const next = (value.bodyHtml || '').slice(0, start) + token + (value.bodyHtml || '').slice(end);
    onChange({ ...value, bodyHtml: next });
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
  };

  // Sample values so the preview reads like a real mail rather than braces.
  const sample: Record<string, string> = {
    name: 'Asha Pillai', firstName: 'Asha', email: 'asha@acme.example',
    company: 'Acme Lubricants', city: 'Pune', phone: '98200 00000',
    companyName: 'RBM Oil', unsubscribeUrl: '#unsubscribe',
  };
  const previewHtml = String(value.bodyHtml || '')
    .replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, k) => sample[k] ?? '');

  return (
    <div className="rounded-xl border border-outline bg-surface p-4 space-y-3">
      <h3 className="text-sm font-bold">{value._id ? 'Edit template' : 'New template'}</h3>

      <div className="grid sm:grid-cols-2 gap-3">
        <Text label="Template name" value={value.name} onChange={(v) => onChange({ ...value, name: v })} />
        <Text label="Default subject" value={value.subject} onChange={(v) => onChange({ ...value, subject: v })} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
          <span className="text-xs font-semibold">Body</span>
          <button onClick={() => setShowPreview(!showPreview)}
            className="text-xs font-semibold text-primary flex items-center gap-1">
            <Eye className="w-3.5 h-3.5" /> {showPreview ? 'Edit HTML' : 'Preview'}
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-2">
          {MERGE_VARS.map((v) => (
            <button key={v} type="button" onClick={() => insert(v)}
              className="px-2 py-1 rounded-md border border-outline text-[11px] font-mono hover:border-primary hover:text-primary">
              {`{{${v}}}`}
            </button>
          ))}
          <button type="button" onClick={() => insert('unsubscribeUrl')}
            className={`px-2 py-1 rounded-md border text-[11px] font-mono ${
              hasUnsub ? 'border-outline' : 'border-danger/40 text-danger-text dark:text-danger-text'
            }`}>
            {'{{unsubscribeUrl}}'}
          </button>
        </div>

        {showPreview ? (
          // Rendered as escaped text, never injected. This is a preview of mail
          // assembled from contact data; the admin page must not execute it.
          <pre className="w-full min-h-[12rem] px-3 py-2 rounded-lg border border-outline bg-surface-variant/20 text-xs whitespace-pre-wrap break-words overflow-auto">
            {previewHtml}
          </pre>
        ) : (
          <textarea
            id="tpl-body"
            value={value.bodyHtml}
            onChange={(e) => onChange({ ...value, bodyHtml: e.target.value })}
            rows={14}
            className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-xs font-mono"
          />
        )}
      </div>

      {!hasUnsub && (
        <div className="p-2.5 rounded-lg bg-danger/10 dark:bg-danger border border-danger/25 dark:border-danger/40 text-xs text-danger-text dark:text-danger-text flex items-start gap-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            This template has no unsubscribe link, so no campaign using it can be sent.
            Add <code>{'{{unsubscribeUrl}}'}</code> as the href of a link.
          </span>
        </div>
      )}

      {unknown.length > 0 && (
        <div className="p-2.5 rounded-lg bg-warning/10 dark:bg-warning border border-warning/25 dark:border-warning/40 text-xs text-warning-text dark:text-warning-text">
          Unknown variable{unknown.length > 1 ? 's' : ''}: {unknown.map((u) => `{{${u}}}`).join(', ')} —
          {' '}these render as nothing. Use the buttons above for supported ones.
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold">
          Cancel
        </button>
        <button onClick={onSave} disabled={!canSave || saving}
          title={!hasUnsub ? 'Add an unsubscribe link first' : undefined}
          className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold disabled:opacity-40">
          Save template
        </button>
      </div>
    </div>
  );
}

// ── Suppressions ─────────────────────────────────────────────────────────────
function SuppressionsTab() {
  const { data } = useQuery({
    queryKey: ['campaigns', 'suppressions'],
    queryFn: async () => (await api.get('/api/campaigns/suppressions')).data,
  });
  const items = data?.items || [];
  return (
    <div className="space-y-3">
      <p className="text-xs text-on-surface-variant">
        These addresses are never mailed, on any list. Opt-outs and hard bounces land here
        automatically.
      </p>
      {items.length === 0 ? <Empty>Nobody is suppressed.</Empty> : (
        <div className="rounded-xl border border-outline overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-surface-variant/30 text-on-surface-variant">
              <tr><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Reason</th><th className="text-left px-4 py-2">When</th></tr>
            </thead>
            <tbody>
              {items.map((s: any) => (
                <tr key={s._id} className="border-t border-outline/50">
                  <td className="px-4 py-2 font-mono">{s.email}</td>
                  <td className="px-4 py-2">{s.reason}</td>
                  <td className="px-4 py-2">{new Date(s.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Bits ─────────────────────────────────────────────────────────────────────
function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-8 text-center border border-dashed border-outline rounded-xl text-sm text-on-surface-variant">
      {children}
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const tone = status === 'sent' ? 'bg-success/10 text-success-text dark:bg-success dark:text-success-text'
    : status === 'sending' ? 'bg-info/10 text-info-text dark:bg-info dark:text-info-text'
    : status === 'failed' ? 'bg-danger/10 text-danger-text dark:bg-danger dark:text-danger-text'
    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${tone}`}>{status}</span>;
}

function Text({ label, value, onChange, hint }: {
  label: string; value: string; onChange: (v: string) => void; hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-semibold block mb-1">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-outline bg-transparent text-sm" />
      {hint && <span className="text-[11px] text-on-surface-variant">{hint}</span>}
    </label>
  );
}

export default CampaignsPage;
